// /tenants/:id — single-tenant detail with operator actions.
//
// Two-column layout for P1:
//   • Left  — info card, subscription card, danger zone (freeze /
//             unfreeze / restore).
//   • Right — counts grid + placeholder cards for the surfaces that
//             land in P3 (usage), P5 (Stripe), P6 (audit timeline).
//
// All mutations route through the typed tenantsApi. After a successful
// mutation we invalidate both the list query and the detail query so
// the list view's frozen badge + the detail header stay coherent.

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Snowflake, Sun, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { useApiClient, useHasStaffScope } from '../../lib/providers.js';
import {
  TENANTS_LIST_QUERY_KEY,
  tenantQueryKey,
  tenantsApi,
  type TenantDetail,
} from '../../lib/api/tenants.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog.js';
import { Label } from '../../components/ui/label.js';
import { Textarea } from '../../components/ui/textarea.js';
import { Skeleton } from '../../components/ui/skeleton.js';
import { ErrorRetry } from '../../components/ui/error-retry.js';
import { formatUsd } from '../../lib/format-money.js';
import { formatBytes } from '../../lib/format-bytes.js';
import { formatDate, formatDateTime } from '../../lib/format-date.js';

export function TenantDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const client = useApiClient();
  const query = useQuery({
    queryKey: tenantQueryKey(id),
    queryFn: () => tenantsApi.detail(client, id),
    enabled: id.length > 0,
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/tenants"
          className="mb-2 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft aria-hidden className="h-3 w-3" />
          Tenants
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">
          {query.data?.name ?? 'Tenant'}
        </h1>
        {query.data?.email && (
          <p className="text-sm text-muted-foreground">{query.data.email}</p>
        )}
      </div>

      {query.isPending ? (
        <DetailSkeleton />
      ) : query.isError ? (
        <ErrorRetry
          title="Failed to load tenant"
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      ) : (
        <DetailGrid tenant={query.data} />
      )}
    </div>
  );
}

function DetailGrid({ tenant }: { tenant: TenantDetail }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-1">
        <InfoCard tenant={tenant} />
        <SubscriptionCard tenant={tenant} />
        <DangerZone tenant={tenant} />
      </div>
      <div className="space-y-4 lg:col-span-2">
        <CountsCard tenant={tenant} />
        <PlaceholderCard
          title="Usage history"
          subPhase="P3"
          body="A 30-day daily-cost line chart and per-agent breakdown will land here when the Money & Usage milestone ships."
        />
        <PlaceholderCard
          title="Activity timeline"
          subPhase="P6"
          body="Combined audit_log + staff_audit_log entries scoped to this tenant will land here once the audit viewer ships."
        />
      </div>
    </div>
  );
}

function InfoCard({ tenant }: { tenant: TenantDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Tenant</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row label="Name" value={tenant.name} />
        <Row label="Email" value={tenant.email ?? '—'} />
        <Row label="Created" value={formatDate(tenant.createdAt)} />
        <Row
          label="Last activity"
          value={tenant.lastActiveAt ? formatDateTime(tenant.lastActiveAt) : 'Never'}
        />
        <Row
          label="Tenant id"
          value={<code className="text-xs">{tenant.id}</code>}
        />
      </CardContent>
    </Card>
  );
}

function SubscriptionCard({ tenant }: { tenant: TenantDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Subscription</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <Row
          label="Plan"
          value={
            tenant.planCode ? <Badge variant="secondary">{tenant.planCode}</Badge> : '—'
          }
        />
        <Row label="Status" value={tenant.subscriptionStatus ?? '—'} />
        <Row label="Cost (30d)" value={formatUsd(tenant.costLast30dUsd)} />
        {tenant.frozenAt && (
          <Row
            label="Frozen"
            value={
              <span className="text-destructive">
                {formatDateTime(tenant.frozenAt)}
                {tenant.frozenReason && (
                  <span className="block text-xs text-muted-foreground">
                    {tenant.frozenReason}
                  </span>
                )}
              </span>
            }
          />
        )}
        {tenant.deletedAt && (
          <Row
            label="Soft-deleted"
            value={
              <span className="text-destructive">
                {formatDateTime(tenant.deletedAt)}
                {tenant.purgeAfter && (
                  <span className="block text-xs text-muted-foreground">
                    Purge after {formatDate(tenant.purgeAfter)}
                  </span>
                )}
              </span>
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

function CountsCard({ tenant }: { tenant: TenantDetail }) {
  const items: { label: string; value: string | number }[] = [
    { label: 'Agents', value: tenant.agentCount },
    { label: 'API keys', value: tenant.apiKeyCount },
    { label: 'Conversations', value: tenant.conversationCount },
    { label: 'Tickets', value: tenant.ticketCount },
    { label: 'KB documents', value: tenant.kbDocCount },
    { label: 'KB storage', value: formatBytes(tenant.kbBytes) },
  ];
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Counts</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {items.map((it) => (
            <div key={it.label} className="space-y-0.5">
              <dt className="text-xs uppercase tracking-wider text-muted-foreground">
                {it.label}
              </dt>
              <dd className="text-lg font-medium">{it.value}</dd>
            </div>
          ))}
        </dl>
      </CardContent>
    </Card>
  );
}

function DangerZone({ tenant }: { tenant: TenantDetail }) {
  const canMutate = useHasStaffScope('staff:platform');
  const isFrozen = tenant.frozenAt !== null;
  const isSoftDeleted = tenant.deletedAt !== null;

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="text-base text-destructive">Danger zone</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!canMutate && (
          <p className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive">
            Read-only — these actions require <code>staff:platform</code>.
          </p>
        )}
        {isFrozen ? (
          <UnfreezeButton tenantId={tenant.id} disabled={!canMutate} />
        ) : (
          <FreezeButton tenantId={tenant.id} disabled={!canMutate} />
        )}
        {isSoftDeleted && (
          <RestoreButton tenantId={tenant.id} disabled={!canMutate} />
        )}
      </CardContent>
    </Card>
  );
}

function FreezeButton({ tenantId, disabled }: { tenantId: string; disabled: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const mut = useMutation({
    mutationFn: (payload: { reason: string }) => tenantsApi.freeze(client, tenantId, payload.reason),
    onSuccess: () => {
      toast.success('Tenant frozen');
      void qc.invalidateQueries({ queryKey: tenantQueryKey(tenantId) });
      void qc.invalidateQueries({ queryKey: TENANTS_LIST_QUERY_KEY });
      setOpen(false);
      setReason('');
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Freeze failed');
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        variant="destructive"
        size="sm"
        disabled={disabled}
        onClick={() => {
          setOpen(true);
        }}
        className="w-full justify-start"
      >
        <Snowflake aria-hidden className="mr-2 h-4 w-4" />
        Freeze tenant
      </Button>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Freeze tenant?</DialogTitle>
          <DialogDescription>
            Every chat call from this tenant will return 402 within ~30s
            (cache TTL). The reason you type lands in <code>staff_audit_log</code>{' '}
            and the customer sees the standard "account frozen" banner.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="freeze-reason">Reason</Label>
          <Textarea
            id="freeze-reason"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder="e.g. cost anomaly investigation, fraud signal, support hold"
          />
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => {
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={reason.trim().length === 0 || mut.isPending}
            onClick={() => {
              mut.mutate({ reason: reason.trim() });
            }}
          >
            {mut.isPending ? 'Freezing…' : 'Freeze'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UnfreezeButton({ tenantId, disabled }: { tenantId: string; disabled: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => tenantsApi.unfreeze(client, tenantId),
    onSuccess: () => {
      toast.success('Tenant unfrozen');
      void qc.invalidateQueries({ queryKey: tenantQueryKey(tenantId) });
      void qc.invalidateQueries({ queryKey: TENANTS_LIST_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Unfreeze failed');
    },
  });

  return (
    <Button
      variant="default"
      size="sm"
      disabled={disabled || mut.isPending}
      onClick={() => {
        mut.mutate();
      }}
      className="w-full justify-start"
    >
      <Sun aria-hidden className="mr-2 h-4 w-4" />
      {mut.isPending ? 'Unfreezing…' : 'Unfreeze tenant'}
    </Button>
  );
}

function RestoreButton({ tenantId, disabled }: { tenantId: string; disabled: boolean }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const mut = useMutation({
    mutationFn: () => tenantsApi.restore(client, tenantId),
    onSuccess: () => {
      toast.success('Tenant restored');
      void qc.invalidateQueries({ queryKey: tenantQueryKey(tenantId) });
      void qc.invalidateQueries({ queryKey: TENANTS_LIST_QUERY_KEY });
    },
    onError: (err: { message?: string }) => {
      toast.error(err.message ?? 'Restore failed');
    },
  });

  return (
    <div className="space-y-2">
      <Button
        variant="default"
        size="sm"
        disabled={disabled || mut.isPending}
        onClick={() => {
          mut.mutate();
        }}
        className="w-full justify-start"
      >
        <RotateCcw aria-hidden className="mr-2 h-4 w-4" />
        {mut.isPending ? 'Restoring…' : 'Restore from soft-delete'}
      </Button>
      <p className="text-xs text-muted-foreground">
        Operator escape-hatch when the customer can no longer reach{' '}
        <button
          type="button"
          className="underline hover:text-foreground"
          onClick={() => {
            navigate('/tenants');
          }}
        >
          their dashboard
        </button>
        . Confirm identity out-of-band before restoring.
      </p>
    </div>
  );
}

function PlaceholderCard({
  title,
  subPhase,
  body,
}: {
  title: string;
  subPhase: string;
  body: string;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{title}</CardTitle>
        <Badge variant="outline">Coming in {subPhase}</Badge>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">{body}</p>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <div className="text-right">{value}</div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-4 lg:col-span-1">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
      <div className="space-y-4 lg:col-span-2">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  );
}
